/**
 * InterChallengeWizard — Tunnel de création de challenge inter-entreprises (6 étapes)
 * 1. Identité  2. Société fictive  3. Offres  4. Personae  5. Cas de simulation  6. Récompense + cadre
 */

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { localizeCompanySize, localizeIndustry } from "../lib/target-options-i18n";
import { pickScoringLabel } from "../lib/scoring-i18n";
import {
  pickChallengeTitle,
  pickChallengeDescription,
  pickChallengeFictiveCompanyName,
  pickChallengeFictiveCompanyDescription,
  pickChallengeGiftName,
  pickChallengeGiftDescription,
} from "../lib/challenge-i18n";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ChevronLeft, ChevronRight, Check, X, Plus, Loader2, AlertCircle, Globe, Users, Trash2, Copy, Sparkles, Pencil } from "lucide-react";
import { useResizable } from "../hooks/useResizable";
import { challengesApi, badgesApi, authApi, interChallengesApi, accountApi } from "../lib/api";
import { AiChat } from "./AiChat";
import {
  PERSONA_FUNCTIONS, CSP_LIST, AGE_RANGES, PURCHASE_CRITERIA, PURCHASE_PHASES,
  CONTACT_REASONS, EXPRESSION_STYLES, MAIN_EXPECTATIONS, DECISION_MAKER_CODES, DECISION_MAKER_LABELS,
  PersonalityStyleSelect, MaturityLevelSelect, InternalPressureSelect, isPersonaComplete,
  personaNeedsPersonalityMigration, getPersonaLabel, pickDecisionMaker,
} from "./PersonaUtils";
import { Badge } from "./ui/badge";

const STEPS = [
  { id: "identity", labelKey: "wizard.interChallenge.stepIdentity" },
  { id: "company", labelKey: "wizard.interChallenge.stepCompany" },
  { id: "offers", labelKey: "wizard.interChallenge.stepOffers" },
  { id: "personae", labelKey: "wizard.interChallenge.stepPersonae" },
  { id: "targets", labelKey: "wizard.interChallenge.stepTargets" },
  { id: "reward", labelKey: "wizard.interChallenge.stepReward" },
];

interface InterChallengeWizardProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  /** Si fourni, le wizard est en mode édition */
  editingChallengeId?: string | null;
  /**
   * Simulateurs mis en avant, hérités de la situation manager. Un challenge est
   * mono-simulateur (`challenges.simulator_id` est NOT NULL) : quand la
   * situation n'en recommande qu'un, il est déjà choisi.
   */
  recommendedSimulators?: string[];
}

export function InterChallengeWizard({ open, onClose, onCreated, editingChallengeId, recommendedSimulators }: InterChallengeWizardProps) {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === "fr";

  const { containerRef, onPointerDown, boxStyle } = useResizable({
    minWidth: 500, minHeight: 550,
    maxWidth: typeof window !== "undefined" ? window.innerWidth - 40 : 1100,
    maxHeight: typeof window !== "undefined" ? window.innerHeight - 40 : 900,
    defaultWidth: 720, defaultHeight: 780,
  });

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Data
  const [simulators, setSimulators] = useState<any[]>([]);
  const [badges, setBadges] = useState<any[]>([]);
  const [existingOffers, setExistingOffers] = useState<any[]>([]);
  const [existingPersonae, setExistingPersonae] = useState<any[]>([]);

  /**
   * Ce qu'on propose de réutiliser, depuis DEUX sources complémentaires.
   *
   * Le tunnel n'interrogeait que `/api/admin/inter-challenge-*`, réservé aux ADM,
   * et avalait le refus (`.catch(() => {})`). Un manager voyait donc « aucune
   * offre existante » alors que son compte en contenait — et devait tout
   * ressaisir. Ouvrir ces routes aux managers n'était pas la solution : elles
   * renvoient des copies d'offres d'AUTRES sociétés, issues de challenges
   * inter-entreprises.
   *
   * On charge donc :
   *  - les offres et personae DU COMPTE, accessibles à tous — c'est ce qu'un
   *    manager veut réutiliser ;
   *  - ceux des challenges inter, en complément, quand le profil y a droit.
   *    L'échec reste silencieux : pour un non-ADM, c'est le cas nominal.
   *
   * La réutilisation COPIE les valeurs dans le challenge (elle ne crée pas de
   * lien), d'où le mapping explicite : on ne recopie que les champs qu'un
   * persona de challenge connaît.
   */
  const chargerReutilisables = async () => {
    const [offresCompte, personaeCompte] = await Promise.all([
      accountApi.getOffers().catch(() => []),
      accountApi.getAccountPersonae().catch(() => []),
    ]);
    const depuisMonCompte = t("wizard.interChallenge.fromMyAccount");

    setExistingOffers([
      ...(offresCompte || []).map((o: any) => ({
        id: `compte-${o.id}`, name: o.name, description: o.description || "", challengeTitle: depuisMonCompte,
      })),
    ]);
    setExistingPersonae([
      ...(personaeCompte || []).map((p: any) => ({
        id: `compte-${p.id}`, challengeTitle: depuisMonCompte,
        personaType: p.personaType, category: p.category,
        function: p.function, csp: p.csp, ageRange: p.ageRange,
        decisionMakerProfile: p.decisionMakerProfile, painPoints: p.painPoints,
        purchaseCriteria: p.purchaseCriteria, contactReason: p.contactReason,
        expressionStyle: p.expressionStyle, mainExpectation: p.mainExpectation,
      })),
    ]);

    // Complément ADM — un refus 403 est le cas normal pour les autres profils.
    interChallengesApi.getOffers()
      .then((o: any[]) => o?.length && setExistingOffers((prev) => [...prev, ...o]))
      .catch(() => {});
    interChallengesApi.getPersonae()
      .then((p: any[]) => p?.length && setExistingPersonae((prev) => [...prev, ...p]))
      .catch(() => {});
  };

  // Form state
  const [form, setForm] = useState({
    title: "", description: "", simulatorId: "",
    startDate: "", endDate: "",
    minSessions: 1, maxSessions: 10,
    primaryObjective: "cumulative_score",
    secondaryObjective: "",
    orangeThresholdMinutes: "",
    redThresholdMinutes: "",
    difficulty: "medium",
    challengeType: "optional",
  });
  const [company, setCompany] = useState({ name: "", description: "" });
  const [offers, setOffers] = useState<Array<{ name: string; description: string }>>([]);
  const [personae, setPersonae] = useState<Array<Record<string, string>>>([]);
  const [targets, setTargets] = useState<Array<{ caseType: string; companySize: string; industry: string[]; offerIndexes: number[]; personaeIndexes: number[] }>>([]);
  const [editingTargetIdx, setEditingTargetIdx] = useState<number | null>(null);
  const [targetForm, setTargetForm] = useState({ caseType: "B2B", companySize: "", industry: [] as string[], offerIndexes: [] as number[], personaeIndexes: [] as number[] });

  const COMPANY_SIZE_OPTIONS = ["Micro-entreprises", "TPE/TPI", "PME/PMI", "ETI", "Grandes entreprises", "Secteur public"];
  const INDUSTRY_OPTIONS = ["Administration - fonction publique", "Agroalimentaire", "Automobile", "Banque / Assurance", "BTP / Construction", "Chimie / Pharmacie", "Commerce / Distribution", "Conseil / Audit", "Éducation / Formation", "Énergie / Environnement", "Immobilier", "Industrie / Manufacture", "Informatique / Tech / SaaS", "Logistique / Transport", "Luxe / Mode", "Médias / Communication", "Santé / Médical", "Services aux entreprises", "Télécom", "Tourisme / Hôtellerie / Restauration"];
  const [reward, setReward] = useState({
    scope: "inter_public",
    badgeId: "", giftName: "", giftDescription: "", giftPhotoUrl: "",
    allowedAccountIds: [] as string[],
  });

  // Sub-views for offers/personae
  const [offerSubView, setOfferSubView] = useState<"list" | "create" | "ai" | "reuse">("list");
  const [personaSubView, setPersonaSubView] = useState<"list" | "create" | "ai" | "reuse">("list");
  const [newOffer, setNewOffer] = useState({ name: "", description: "" });
  const [newPersona, setNewPersona] = useState<Record<string, string>>({ personaType: "B2B", category: "sales", function: "" });
  const [customFnMode, setCustomFnMode] = useState(false);
  const [editingOfferIdx, setEditingOfferIdx] = useState<number | null>(null);
  const [editingPersonaIdx, setEditingPersonaIdx] = useState<number | null>(null);

  const [loadingEdit, setLoadingEdit] = useState(false);
  const isEditing = !!editingChallengeId;

  useEffect(() => {
    if (open) {
      authApi.getAgents().then((a: any[]) => {
        const list = a || [];
        // Les recommandés en tête, et le seul recommandé directement retenu :
        // c'est la contrepartie d'un challenge mono-simulateur.
        const reco = new Set(recommendedSimulators ?? []);
        if (reco.size > 0) {
          list.sort((x: any, y: any) => Number(reco.has(y.name)) - Number(reco.has(x.name)));
          const matches = list.filter((x: any) => reco.has(x.name));
          // En édition, on ne touche pas au simulateur déjà enregistré.
          if (matches.length === 1 && !editingChallengeId) {
            setForm((f: any) => (f.simulatorId ? f : { ...f, simulatorId: matches[0].id }));
          }
        }
        setSimulators(list);
      }).catch(() => {});
      badgesApi.getAll().then((b: any[]) => setBadges(b || [])).catch(() => {});
      void chargerReutilisables();

      // Mode édition : charger les données du challenge
      if (editingChallengeId) {
        setLoadingEdit(true);
        challengesApi.getChallenge(editingChallengeId).then((ch: any) => {
          // Étape 1 — Identité
          setForm({
            // Préremplissage i18n : on charge la variante FR/EN correspondant à la
            // langue de l'éditeur (fallback cascade via les helpers challenge-i18n).
            title: pickChallengeTitle(ch, i18n.language),
            description: pickChallengeDescription(ch, i18n.language),
            simulatorId: ch.simulatorId || "",
            startDate: ch.startDate ? ch.startDate.split("T")[0] : "",
            endDate: ch.endDate ? ch.endDate.split("T")[0] : "",
            minSessions: ch.minSessions || 1,
            maxSessions: ch.maxSessions || 10,
            primaryObjective: ch.primaryObjective || "cumulative_score",
            secondaryObjective: ch.secondaryObjective || "",
            orangeThresholdMinutes: ch.orangeThresholdMinutes ? String(ch.orangeThresholdMinutes) : "",
            redThresholdMinutes: ch.redThresholdMinutes ? String(ch.redThresholdMinutes) : "",
            challengeType: ch.challengeType || "optional",
            difficulty: ch.difficulty || "medium",
          });
          // Étape 2 — Société fictive
          setCompany({ name: pickChallengeFictiveCompanyName(ch, i18n.language), description: pickChallengeFictiveCompanyDescription(ch, i18n.language) });
          // Étape 3 — Offres
          setOffers((ch.offers || []).map((o: any) => ({ name: o.name, description: o.description || "" })));
          // Étape 4 — Personae
          setPersonae((ch.personae || []).map((p: any) => {
            const { id, challengeId, position, ...rest } = p;
            return rest;
          }));
          // Étape 5 — Cas de simulation
          setTargets(ch.interTargets || []);
          // Étape 6 — Récompense + cadre
          setReward({
            scope: ch.scope || "inter_public",
            badgeId: ch.badgeId || "",
            giftName: pickChallengeGiftName(ch, i18n.language),
            giftDescription: pickChallengeGiftDescription(ch, i18n.language),
            giftPhotoUrl: ch.giftPhotoUrl || "",
            allowedAccountIds: ch.allowedAccountIds || [],
          });
          setStep(0);
        }).catch((err: any) => {
          setError(err.message);
        }).finally(() => setLoadingEdit(false));
      } else {
        // Mode création : reset
        setForm({ title: "", description: "", simulatorId: "", startDate: "", endDate: "", minSessions: 1, maxSessions: 10, primaryObjective: "cumulative_score", secondaryObjective: "", orangeThresholdMinutes: "", redThresholdMinutes: "", difficulty: "medium", challengeType: "optional" });
        setCompany({ name: "", description: "" });
        setOffers([]);
        setPersonae([]);
        setTargets([]);
        setReward({ scope: "inter_public", badgeId: "", giftName: "", giftDescription: "", giftPhotoUrl: "", allowedAccountIds: [] });
        setStep(0);
        setError("");
      }
    }
  }, [open, editingChallengeId]);

  const canNext = () => {
    if (step === 0) return form.title.trim() && form.simulatorId && form.startDate && form.endDate;
    if (step === 1) return company.name.trim();
    if (step === 2) return offers.length > 0;
    if (step === 3) return personae.length > 0;
    if (step === 4) return true; // targets optionnels
    if (step === 5) return true;
    return true;
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        orangeThresholdMinutes: form.orangeThresholdMinutes ? parseInt(form.orangeThresholdMinutes) : undefined,
        redThresholdMinutes: form.redThresholdMinutes ? parseInt(form.redThresholdMinutes) : undefined,
        secondaryObjective: form.secondaryObjective || undefined,
        scope: reward.scope,
        fictiveCompanyName: company.name,
        fictiveCompanyDescription: company.description,
        badgeId: reward.badgeId || undefined,
        giftName: reward.giftName || undefined,
        giftDescription: reward.giftDescription || undefined,
        giftPhotoUrl: reward.giftPhotoUrl || undefined,
        offers: offers.filter(o => o.name.trim()),
        personae,
        interTargets: targets.length > 0 ? targets : undefined,
        allowedAccountIds: reward.scope === "inter_cross_clients" ? reward.allowedAccountIds : undefined,
      } as any;

      if (isEditing && editingChallengeId) {
        await challengesApi.updateChallenge(editingChallengeId, payload);
      } else {
        await challengesApi.createChallenge(payload);
      }
      onCreated();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true" onClick={() => { if (!document.body.style.cursor) onClose(); }}>
      <div ref={containerRef} onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
        style={boxStyle(720, 780)}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b shrink-0" style={{ backgroundColor: "#F6C847" + "20" }}>
          <div>
            <h2 className="font-bold text-lg" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>
              {isEditing
                ? t("wizard.interChallenge.titleEdit")
                : t("wizard.interChallenge.titleCreate")}
            </h2>
            <p className="text-xs text-muted-foreground">
              {t("wizard.interChallenge.stepProgress", { current: step + 1, total: STEPS.length, label: t(STEPS[step].labelKey) })}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>

        {/* Progress bar */}
        <div className="flex gap-1 px-6 py-2 shrink-0">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex-1 h-1.5 rounded-full transition-colors"
              style={{ backgroundColor: i <= step ? "#F6C847" : "#E5E7EB" }} />
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">

          {/* ===== STEP 1: Identité ===== */}
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">{t("wizard.interChallenge.challengeTitleLabel")}</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder={t("wizard.interChallenge.challengeTitlePlaceholder")} />
              </div>
              <div>
                <Label className="text-xs">{t("wizard.interChallenge.descriptionLabel")}</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder={t("wizard.interChallenge.descriptionPlaceholder")} rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("wizard.interChallenge.simulatorLabel")}</Label>
                  <Select value={form.simulatorId} onValueChange={(v) => setForm({ ...form, simulatorId: v })}>
                    <SelectTrigger><SelectValue placeholder={t("wizard.interChallenge.choosePlaceholder")} /></SelectTrigger>
                    <SelectContent>{simulators.map((s: any) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                        {(recommendedSimulators ?? []).includes(s.name) && ` · ${t("wizard.simulationCase.recommended")}`}
                      </SelectItem>
                    ))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("wizard.interChallenge.enrollmentTypeLabel")}</Label>
                  <Select value={form.challengeType} onValueChange={(v) => setForm({ ...form, challengeType: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="optional">{t("wizard.interChallenge.enrollmentOptional")}</SelectItem>
                      <SelectItem value="mandatory">{t("wizard.interChallenge.enrollmentMandatory")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("wizard.interChallenge.startDateLabel")}</Label>
                  <Input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">{t("wizard.interChallenge.endDateLabel")}</Label>
                  <Input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">{t("wizard.interChallenge.minSessionsLabel")}</Label>
                  <Input type="number" min={1} value={form.minSessions} onChange={(e) => setForm({ ...form, minSessions: parseInt(e.target.value) || 1 })} />
                </div>
                <div>
                  <Label className="text-xs">{t("wizard.interChallenge.maxSessionsLabel")}</Label>
                  <Input type="number" min={1} value={form.maxSessions} onChange={(e) => setForm({ ...form, maxSessions: parseInt(e.target.value) || 10 })} />
                </div>
              </div>
              {/* Objectifs — dynamiques selon le simulateur sélectionné */}
              {(() => {
                const selectedSim = simulators.find((s: any) => s.id === form.simulatorId);
                const simName = selectedSim?.name?.toLowerCase() || "";
                const SIMULATOR_OBJECTIVES: Record<string, Array<{ value: string; labelKey: string }>> = {
                  prospector: [{ value: "most_rdv", labelKey: "challenge.objectiveMostRdv" }],
                  discovery: [{ value: "most_needs", labelKey: "challenge.objectiveMostNeeds" }],
                  negociator: [{ value: "deal_closed", labelKey: "challenge.objectiveDealClosed" }],
                  pitch: [{ value: "pitch_validated", labelKey: "challenge.objectivePitchValidated" }],
                  satisfactor: [
                    { value: "tension_mastered", labelKey: "challenge.objectiveTensionMastered" },
                    { value: "clear_outcome", labelKey: "challenge.objectiveClearOutcome" },
                  ],
                };
                const specificObjectives = (SIMULATOR_OBJECTIVES[simName] || []).map(o => ({ value: o.value, label: t(o.labelKey) }));
                const overallKeys = ["overall", "global", "score_total", "total_score"];
                const scoringObjectives = (selectedSim?.scoringCriteria || [])
                  .filter((c: any) => c.type === "score" && !overallKeys.includes(c.key))
                  .map((c: any) => ({ value: c.key, label: pickScoringLabel(c, isFr ? "fr" : "en") }));
                const commonObjectives = [
                  { value: "cumulative_score", label: t("challenge.objectiveCumulativeScore") },
                  { value: "best_average", label: t("challenge.objectiveBestAverage") },
                ];
                const availableObjectives = [...specificObjectives, ...scoringObjectives, ...commonObjectives];
                return (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">{t("wizard.interChallenge.primaryObjectiveLabel")}</Label>
                      <Select value={form.primaryObjective} onValueChange={(v) => setForm({ ...form, primaryObjective: v })} disabled={!form.simulatorId}>
                        <SelectTrigger><SelectValue placeholder={form.simulatorId ? t("wizard.interChallenge.selectPlaceholder") : t("wizard.interChallenge.chooseSimulatorFirst")} /></SelectTrigger>
                        <SelectContent>
                          {availableObjectives.map((obj) => <SelectItem key={obj.value} value={obj.value}>{obj.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">{t("wizard.interChallenge.secondaryObjectiveLabel")}</Label>
                      <Select value={form.secondaryObjective || "_none"} onValueChange={(v) => setForm({ ...form, secondaryObjective: v === "_none" ? "" : v })} disabled={!form.simulatorId}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">{t("wizard.interChallenge.noneOption")}</SelectItem>
                          {availableObjectives.filter((obj) => obj.value !== form.primaryObjective).map((obj) => <SelectItem key={obj.value} value={obj.value}>{obj.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                );
              })()}
              {/* Seuils */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("wizard.interChallenge.orangeThresholdLabel")}</Label>
                  <Input type="number" min={0} value={form.orangeThresholdMinutes}
                    onChange={(e) => setForm({ ...form, orangeThresholdMinutes: e.target.value })}
                    placeholder={t("wizard.interChallenge.orangeThresholdPlaceholder")} />
                </div>
                <div>
                  <Label className="text-xs">{t("wizard.interChallenge.redThresholdLabel")}</Label>
                  <Input type="number" min={0} value={form.redThresholdMinutes}
                    onChange={(e) => setForm({ ...form, redThresholdMinutes: e.target.value })}
                    placeholder={t("wizard.interChallenge.redThresholdPlaceholder")} />
                </div>
              </div>
              {/* Niveau de difficulté */}
              <div>
                <Label className="text-xs">{t("wizard.interChallenge.difficultyLabel")}</Label>
                <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">{t("wizard.interChallenge.difficultyEasy")}</SelectItem>
                    <SelectItem value="medium">{t("wizard.interChallenge.difficultyMedium")}</SelectItem>
                    <SelectItem value="hard">{t("wizard.interChallenge.difficultyHard")}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {t("wizard.interChallenge.difficultyHint")}
                </p>
              </div>
            </div>
          )}

          {/* ===== STEP 2: Société fictive ===== */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t("wizard.interChallenge.companyIntro")}
              </p>
              <div>
                <Label className="text-xs">{t("wizard.interChallenge.companyNameLabel")}</Label>
                <Input value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })}
                  placeholder={t("wizard.interChallenge.companyNamePlaceholder")} />
              </div>
              <div>
                <Label className="text-xs">{t("wizard.interChallenge.companyDescriptionLabel")}</Label>
                <Textarea value={company.description} onChange={(e) => setCompany({ ...company, description: e.target.value })}
                  placeholder={t("wizard.interChallenge.companyDescriptionPlaceholder")} rows={5} />
              </div>
            </div>
          )}

          {/* ===== STEP 3: Offres ===== */}
          {step === 2 && (
            <div className="space-y-3">
              {/* Mode switcher */}
              {offerSubView === "list" && (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">{t("wizard.interChallenge.offersCount", { count: offers.length })}</p>
                  </div>
                  {/* Existing offers */}
                  {offers.map((o, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg border bg-green-50/50">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{o.name}</p>
                        {o.description && <p className="text-xs text-muted-foreground truncate">{o.description}</p>}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => { setNewOffer({ ...o }); setEditingOfferIdx(i); setOfferSubView("create"); }}><Pencil className="h-3 w-3 text-gray-500" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setOffers(offers.filter((_, j) => j !== i))}><Trash2 className="h-3 w-3 text-red-400" /></Button>
                    </div>
                  ))}
                  {/* Action buttons */}
                  <div className="grid grid-cols-3 gap-2 pt-2">
                    <button onClick={() => { setNewOffer({ name: "", description: "" }); setEditingOfferIdx(null); setOfferSubView("create"); }}
                      className="flex flex-col items-center gap-1 p-3 rounded-xl border-2 border-gray-200 hover:border-[#7CBF94] hover:bg-green-50 transition-all text-center">
                      <Plus className="h-5 w-5" style={{ color: "#7CBF94" }} />
                      <span className="text-xs font-medium">{t("common.create")}</span>
                    </button>
                    <button onClick={() => setOfferSubView("ai")}
                      className="flex flex-col items-center gap-1 p-3 rounded-xl border-2 border-gray-200 hover:border-[#988EF2] hover:bg-purple-50 transition-all text-center">
                      <Sparkles className="h-5 w-5" style={{ color: "#988EF2" }} />
                      <span className="text-xs font-medium">{t("wizard.interChallenge.aiAgent")}</span>
                    </button>
                    <button onClick={() => setOfferSubView("reuse")}
                      className="flex flex-col items-center gap-1 p-3 rounded-xl border-2 border-gray-200 hover:border-[#A6D3F9] hover:bg-blue-50 transition-all text-center">
                      <Copy className="h-5 w-5" style={{ color: "#384872" }} />
                      <span className="text-xs font-medium">{t("wizard.interChallenge.reuse")}</span>
                    </button>
                  </div>
                </>
              )}

              {/* Create offer inline */}
              {offerSubView === "create" && (
                <div className="space-y-3">
                  <button onClick={() => setOfferSubView("list")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ChevronLeft className="h-3 w-3" />{t("common.back")}</button>
                  <div>
                    <Label className="text-xs">{t("wizard.interChallenge.offerNameLabel")}</Label>
                    <Input value={newOffer.name} onChange={(e) => setNewOffer({ ...newOffer, name: e.target.value })} placeholder={t("wizard.interChallenge.offerNamePlaceholder")} />
                  </div>
                  <div>
                    <Label className="text-xs">{t("wizard.interChallenge.descriptionLabel")}</Label>
                    <Textarea value={newOffer.description} onChange={(e) => setNewOffer({ ...newOffer, description: e.target.value })} rows={3} />
                  </div>
                  <Button disabled={!newOffer.name.trim()} onClick={() => {
                    if (editingOfferIdx !== null) {
                      const upd = [...offers]; upd[editingOfferIdx] = { ...newOffer }; setOffers(upd);
                    } else {
                      setOffers([...offers, { ...newOffer }]);
                    }
                    setEditingOfferIdx(null); setOfferSubView("list");
                  }} className="bg-[#7CBF94] hover:bg-[#7CBF94]/90 text-white">
                    {editingOfferIdx !== null
                      ? <><Check className="h-4 w-4 mr-1" />{t("common.save")}</>
                      : <><Plus className="h-4 w-4 mr-1" />{t("wizard.interChallenge.addOffer")}</>}
                  </Button>
                </div>
              )}

              {/* AI offer creation */}
              {offerSubView === "ai" && (
                <div className="space-y-2">
                  <button onClick={() => setOfferSubView("list")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ChevronLeft className="h-3 w-3" />{t("common.back")}</button>
                  <div style={{ height: "400px" }}>
                    <AiChat
                      agentCode="offer_creator" robotColor="#7CBF94" bgColor="#f7faf8"
                      context={{ companyDescription: company.description || company.name, language: i18n.language }}
                      onItemCreated={(_type, data) => { setOffers((prev) => [...prev, { name: data.name, description: data.description || "" }]); }}
                      onComplete={() => setOfferSubView("list")}
                      title={t("wizard.interChallenge.createOffersWithAi")}
                      subtitle={t("wizard.interChallenge.createOffersWithAiSubtitle")}
                    />
                  </div>
                </div>
              )}

              {/* Reuse from existing */}
              {offerSubView === "reuse" && (
                <div className="space-y-2">
                  <button onClick={() => setOfferSubView("list")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ChevronLeft className="h-3 w-3" />{t("common.back")}</button>
                  <p className="text-xs text-muted-foreground">{t("wizard.interChallenge.reuseOfferHint")}</p>
                  {existingOffers.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">{t("wizard.interChallenge.noExistingOffers")}</p>
                  ) : (
                    <div className="max-h-[300px] overflow-y-auto space-y-1">
                      {existingOffers.map((o: any) => (
                        <button key={o.id} onClick={() => { setOffers([...offers, { name: o.name, description: o.description || "" }]); setOfferSubView("list"); }}
                          className="w-full text-left p-2 rounded-lg border hover:border-[#7CBF94] hover:bg-green-50 transition-all">
                          <p className="text-sm font-medium">{o.name}</p>
                          <p className="text-[10px] text-muted-foreground">{t("wizard.interChallenge.from")}: {o.challengeTitle}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ===== STEP 4: Personae ===== */}
          {step === 3 && (
            <div className="space-y-3">
              {personaSubView === "list" && (
                <>
                  <p className="text-sm text-muted-foreground">{t("wizard.interChallenge.personaeCount", { count: personae.length })}</p>
                  {personae.map((p, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg border bg-purple-50/50">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{getPersonaLabel(p) || t("wizard.interChallenge.personaLabel")} ({p.personaType} — {p.category === "sales" ? t("wizard.interChallenge.contextSales") : t("wizard.interChallenge.contextCustomerCare")})</p>
                        {p.decisionMakerProfile && <p className="text-xs text-muted-foreground">{pickDecisionMaker(p, isFr ? "fr" : "en")}</p>}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => { setNewPersona({ ...p }); setEditingPersonaIdx(i); setCustomFnMode(p.function ? !PERSONA_FUNCTIONS.includes(p.function) : false); setPersonaSubView("create"); }}><Pencil className="h-3 w-3 text-gray-500" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setPersonae(personae.filter((_, j) => j !== i))}><Trash2 className="h-3 w-3 text-red-400" /></Button>
                    </div>
                  ))}
                  <div className="grid grid-cols-3 gap-2 pt-2">
                    <button onClick={() => { setNewPersona({ personaType: "B2B", category: "sales", function: "" }); setEditingPersonaIdx(null); setCustomFnMode(false); setPersonaSubView("create"); }}
                      className="flex flex-col items-center gap-1 p-3 rounded-xl border-2 border-gray-200 hover:border-[#988EF2] hover:bg-purple-50 transition-all text-center">
                      <Plus className="h-5 w-5" style={{ color: "#988EF2" }} />
                      <span className="text-xs font-medium">{t("common.create")}</span>
                    </button>
                    <button onClick={() => setPersonaSubView("ai")}
                      className="flex flex-col items-center gap-1 p-3 rounded-xl border-2 border-gray-200 hover:border-[#988EF2] hover:bg-purple-50 transition-all text-center">
                      <Sparkles className="h-5 w-5" style={{ color: "#988EF2" }} />
                      <span className="text-xs font-medium">{t("wizard.interChallenge.aiAgent")}</span>
                    </button>
                    <button onClick={() => setPersonaSubView("reuse")}
                      className="flex flex-col items-center gap-1 p-3 rounded-xl border-2 border-gray-200 hover:border-[#A6D3F9] hover:bg-blue-50 transition-all text-center">
                      <Copy className="h-5 w-5" style={{ color: "#384872" }} />
                      <span className="text-xs font-medium">{t("wizard.interChallenge.reuse")}</span>
                    </button>
                  </div>
                </>
              )}

              {personaSubView === "create" && (() => {
                const isB2C = newPersona.personaType === "B2C";
                const isCC = newPersona.category === "customer_care";
                const isB2BSales = !isB2C && !isCC;
                const isB2CSales = isB2C && !isCC;
                const showFunction = !isB2C; // B2B Sales + B2B CC
                return (
                <div className="space-y-3">
                  <button onClick={() => setPersonaSubView("list")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ChevronLeft className="h-3 w-3" />{t("common.back")}</button>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">{t("wizard.interChallenge.clientTypeLabel")}</Label>
                      <Select value={newPersona.personaType} onValueChange={(v) => setNewPersona({ ...newPersona, personaType: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="B2B">{t("wizard.interChallenge.clientTypeB2B")}</SelectItem>
                          <SelectItem value="B2C">{t("wizard.interChallenge.clientTypeB2C")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">{t("wizard.interChallenge.contextLabel")}</Label>
                      <Select value={newPersona.category} onValueChange={(v) => setNewPersona({ ...newPersona, category: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sales">{t("wizard.interChallenge.contextSales")}</SelectItem>
                          <SelectItem value="customer_care">{t("wizard.interChallenge.contextCustomerCare")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* === B2B Sales === */}
                  {isB2BSales && (
                    <>
                      <div>
                        <Label className="text-xs">{t("wizard.interChallenge.functionLabel")}</Label>
                        <Select value={PERSONA_FUNCTIONS.includes(newPersona.function || "") ? newPersona.function : customFnMode || (newPersona.function && !PERSONA_FUNCTIONS.includes(newPersona.function)) ? "_custom" : ""} onValueChange={(v) => {
                          if (v === "_custom") { setCustomFnMode(true); }
                          else { setCustomFnMode(false); setNewPersona({ ...newPersona, function: v }); }
                        }}>
                          <SelectTrigger><SelectValue placeholder={t("wizard.interChallenge.selectPlaceholder")} /></SelectTrigger>
                          <SelectContent>
                            {PERSONA_FUNCTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                            <SelectItem value="_custom">{t("wizard.interChallenge.otherType")}</SelectItem>
                          </SelectContent>
                        </Select>
                        {(customFnMode || (newPersona.function && !PERSONA_FUNCTIONS.includes(newPersona.function))) && (
                          <Input value={newPersona.function || ""} onChange={(e) => setNewPersona({ ...newPersona, function: e.target.value })} placeholder={t("wizard.interChallenge.enterFunction")} className="mt-1" />
                        )}
                      </div>
                      <div>
                        <Label className="text-xs">{t("wizard.interChallenge.decisionMakerLabel")}</Label>
                        <Select value={newPersona.decisionMakerProfile || ""} onValueChange={(v) => setNewPersona({ ...newPersona, decisionMakerProfile: v })}>
                          <SelectTrigger><SelectValue placeholder={t("wizard.interChallenge.selectPlaceholder")} /></SelectTrigger>
                          <SelectContent>{DECISION_MAKER_CODES.map((c) => <SelectItem key={c} value={c}>{DECISION_MAKER_LABELS[c]?.[isFr ? "fr" : "en"] || c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">{t("wizard.interChallenge.painPointsLabel")}</Label>
                        <Input value={newPersona.painPoints || ""} onChange={(e) => setNewPersona({ ...newPersona, painPoints: e.target.value })}
                          placeholder={t("wizard.interChallenge.painPointsPlaceholder")} />
                      </div>
                      {/* Section "Réalisme" (compléments) */}
                      <div className="pt-1">
                        <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("persona.sectionRealism")}</Label>
                      </div>
                      <div>
                        <Label className="text-xs">{t("persona.personalMotivations")}</Label>
                        <Textarea value={newPersona.personalMotivations || ""} onChange={(e) => setNewPersona({ ...newPersona, personalMotivations: e.target.value })} placeholder={t("persona.personalMotivationsHint")} rows={2} />
                      </div>
                      <div>
                        <Label className="text-xs">{t("persona.dailyIrritants")}</Label>
                        <Textarea value={newPersona.dailyIrritants || ""} onChange={(e) => setNewPersona({ ...newPersona, dailyIrritants: e.target.value })} placeholder={t("persona.dailyIrritantsHint")} rows={2} />
                      </div>
                      <div>
                        <Label className="text-xs">{t("persona.typicalObjections")}</Label>
                        <Textarea value={newPersona.typicalObjections || ""} onChange={(e) => setNewPersona({ ...newPersona, typicalObjections: e.target.value })} placeholder={t("persona.typicalObjectionsHint")} rows={2} />
                      </div>
                      <div>
                        <Label className="text-xs">{t("persona.neverDisclose")}</Label>
                        <Textarea value={newPersona.neverDisclose || ""} onChange={(e) => setNewPersona({ ...newPersona, neverDisclose: e.target.value })} placeholder={t("persona.neverDiscloseHint")} rows={2} />
                      </div>
                      {/* Section "Psychologie" */}
                      <div className="pt-1">
                        <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("persona.sectionPsychology")}</Label>
                      </div>
                      <div>
                        <Label className="text-xs">{t("persona.personalityStyle")}</Label>
                        <PersonalityStyleSelect value={newPersona.personalityStyle} onChange={(v) => setNewPersona({ ...newPersona, personalityStyle: v })} />
                      </div>
                      <div>
                        <Label className="text-xs">{t("persona.maturityLevel")}</Label>
                        <MaturityLevelSelect value={newPersona.maturityLevel} onChange={(v) => setNewPersona({ ...newPersona, maturityLevel: v })} />
                      </div>
                      <div>
                        <Label className="text-xs">{t("persona.internalPressure")}</Label>
                        <InternalPressureSelect value={newPersona.internalPressure} onChange={(v) => setNewPersona({ ...newPersona, internalPressure: v })} />
                      </div>
                    </>
                  )}

                  {/* === B2C Sales === */}
                  {isB2CSales && (
                    <>
                      <div>
                        <Label className="text-xs">{t("wizard.interChallenge.cspLabel")}</Label>
                        <Select value={newPersona.csp || ""} onValueChange={(v) => setNewPersona({ ...newPersona, csp: v })}>
                          <SelectTrigger><SelectValue placeholder={t("wizard.interChallenge.selectPlaceholder")} /></SelectTrigger>
                          <SelectContent>{CSP_LIST.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">{t("wizard.interChallenge.ageRangeLabel")}</Label>
                        <Select value={newPersona.ageRange || ""} onValueChange={(v) => setNewPersona({ ...newPersona, ageRange: v })}>
                          <SelectTrigger><SelectValue placeholder={t("wizard.interChallenge.selectPlaceholder")} /></SelectTrigger>
                          <SelectContent>{AGE_RANGES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">{t("wizard.interChallenge.purchaseCriteriaLabel")}</Label>
                        <Select value={newPersona.purchaseCriteria || ""} onValueChange={(v) => setNewPersona({ ...newPersona, purchaseCriteria: v })}>
                          <SelectTrigger><SelectValue placeholder={t("wizard.interChallenge.selectPlaceholder")} /></SelectTrigger>
                          <SelectContent>{PURCHASE_CRITERIA.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">{t("wizard.interChallenge.purchasePhaseLabel")}</Label>
                        <Select value={newPersona.purchasePhase || ""} onValueChange={(v) => setNewPersona({ ...newPersona, purchasePhase: v })}>
                          <SelectTrigger><SelectValue placeholder={t("wizard.interChallenge.selectPlaceholder")} /></SelectTrigger>
                          <SelectContent>{PURCHASE_PHASES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {/* « Rapport au produit/service » retiré le 14/09/2026, comme dans le
                          formulaire de persona : ses options étaient des titres de sujets, que
                          les quatre champs de réalisme couvrent en texte libre. Cet assistant
                          crée de vrais personas — le laisser ici aurait redemandé, pour un
                          challenge inter, une information qu'on ne demande plus ailleurs. */}
                      {/* Style de personnalité (obligatoire) */}
                      <div>
                        <Label className="text-xs">{t("persona.personalityStyle")}</Label>
                        <PersonalityStyleSelect value={newPersona.personalityStyle} onChange={(v) => setNewPersona({ ...newPersona, personalityStyle: v })} />
                      </div>
                    </>
                  )}

                  {/* === B2B Customer Care === */}
                  {!isB2C && isCC && (
                    <>
                      <div>
                        <Label className="text-xs">{t("wizard.interChallenge.functionLabel")}</Label>
                        <Select value={PERSONA_FUNCTIONS.includes(newPersona.function || "") ? newPersona.function : customFnMode || (newPersona.function && !PERSONA_FUNCTIONS.includes(newPersona.function)) ? "_custom" : ""} onValueChange={(v) => {
                          if (v === "_custom") { setCustomFnMode(true); }
                          else { setCustomFnMode(false); setNewPersona({ ...newPersona, function: v }); }
                        }}>
                          <SelectTrigger><SelectValue placeholder={t("wizard.interChallenge.selectPlaceholder")} /></SelectTrigger>
                          <SelectContent>
                            {PERSONA_FUNCTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                            <SelectItem value="_custom">{t("wizard.interChallenge.otherType")}</SelectItem>
                          </SelectContent>
                        </Select>
                        {(customFnMode || (newPersona.function && !PERSONA_FUNCTIONS.includes(newPersona.function))) && (
                          <Input value={newPersona.function || ""} onChange={(e) => setNewPersona({ ...newPersona, function: e.target.value })} placeholder={t("wizard.interChallenge.enterFunction")} className="mt-1" />
                        )}
                      </div>
                      <div>
                        <Label className="text-xs">{t("wizard.interChallenge.decisionMakerLabel")}</Label>
                        <Select value={newPersona.decisionMakerProfile || ""} onValueChange={(v) => setNewPersona({ ...newPersona, decisionMakerProfile: v })}>
                          <SelectTrigger><SelectValue placeholder={t("wizard.interChallenge.selectPlaceholder")} /></SelectTrigger>
                          <SelectContent>{DECISION_MAKER_CODES.map((c) => <SelectItem key={c} value={c}>{DECISION_MAKER_LABELS[c]?.[isFr ? "fr" : "en"] || c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* === Customer Care fields (B2B CC + B2C CC) === */}
                  {isCC && (
                    <>
                      <div>
                        <Label className="text-xs">{t("wizard.interChallenge.contactReasonLabel")}</Label>
                        <Select value={newPersona.contactReason || ""} onValueChange={(v) => setNewPersona({ ...newPersona, contactReason: v })}>
                          <SelectTrigger><SelectValue placeholder={t("wizard.interChallenge.selectPlaceholder")} /></SelectTrigger>
                          <SelectContent>{CONTACT_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {/* Nouveau : Style de personnalité (remplace le Style d'expression) */}
                      <div>
                        <Label className="text-xs">{t("persona.personalityStyle")}</Label>
                        <PersonalityStyleSelect value={newPersona.personalityStyle} onChange={(v) => setNewPersona({ ...newPersona, personalityStyle: v })} />
                      </div>
                      {/* Ancien Style d'expression — conservé visible le temps de la migration */}
                      <div>
                        <div className="flex items-center gap-2">
                          <Label className="text-xs">{t("wizard.interChallenge.expressionStyleLabel")}</Label>
                          {personaNeedsPersonalityMigration(newPersona) && (
                            <Badge variant="outline" className="text-[10px] border-[#926B00] text-[#926B00]">{t("persona.tagToMigrate")}</Badge>
                          )}
                        </div>
                        <Select value={newPersona.expressionStyle || ""} onValueChange={(v) => setNewPersona({ ...newPersona, expressionStyle: v })}>
                          <SelectTrigger><SelectValue placeholder={t("wizard.interChallenge.selectPlaceholder")} /></SelectTrigger>
                          <SelectContent>
                            {EXPRESSION_STYLES.map((s) => (
                              <SelectItem key={s.value} value={s.value}>
                                <span className="font-medium">{s.value}</span> <span className="text-muted-foreground text-xs">— {s.description}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[11px] text-muted-foreground italic">{t("persona.expressionStyleTransitionNote")}</p>
                      </div>
                      <div>
                        <Label className="text-xs">{t("wizard.interChallenge.mainExpectationLabel")}</Label>
                        <Select value={newPersona.mainExpectation || ""} onValueChange={(v) => setNewPersona({ ...newPersona, mainExpectation: v })}>
                          <SelectTrigger><SelectValue placeholder={t("wizard.interChallenge.selectPlaceholder")} /></SelectTrigger>
                          <SelectContent>{MAIN_EXPECTATIONS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  <Button disabled={!isPersonaComplete(newPersona)}
                    onClick={() => {
                      if (editingPersonaIdx !== null) {
                        const upd = [...personae]; upd[editingPersonaIdx] = { ...newPersona }; setPersonae(upd);
                      } else {
                        setPersonae([...personae, { ...newPersona }]);
                      }
                      setEditingPersonaIdx(null); setPersonaSubView("list");
                    }}
                    className="bg-[#988EF2] hover:bg-[#988EF2]/90 text-white">
                    {editingPersonaIdx !== null
                      ? <><Check className="h-4 w-4 mr-1" />{t("common.save")}</>
                      : <><Plus className="h-4 w-4 mr-1" />{t("wizard.interChallenge.addPersona")}</>}
                  </Button>
                </div>
                );
              })()}

              {personaSubView === "ai" && (
                <div className="space-y-2">
                  <button onClick={() => setPersonaSubView("list")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ChevronLeft className="h-3 w-3" />{t("common.back")}</button>
                  <div style={{ height: "400px" }}>
                    <AiChat
                      agentCode="persona_creator" robotColor="#988EF2" bgColor="#f8f5ff"
                      context={{ companyDescription: company.description || company.name, offers: offers.map(o => `${o.name}: ${o.description}`).join("\n"), language: i18n.language }}
                      onItemCreated={(_type, data) => { setPersonae((prev) => [...prev, data]); }}
                      onComplete={() => setPersonaSubView("list")}
                      title={t("wizard.interChallenge.createPersonaeWithAi")}
                      subtitle={t("wizard.interChallenge.createPersonaeWithAiSubtitle")}
                    />
                  </div>
                </div>
              )}

              {personaSubView === "reuse" && (
                <div className="space-y-2">
                  <button onClick={() => setPersonaSubView("list")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ChevronLeft className="h-3 w-3" />{t("common.back")}</button>
                  {existingPersonae.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">{t("wizard.interChallenge.noExistingPersonae")}</p>
                  ) : (
                    <div className="max-h-[300px] overflow-y-auto space-y-1">
                      {existingPersonae.map((p: any) => (
                        <button key={p.id} onClick={() => {
                          const { id, challengeId, challengeTitle, ...rest } = p;
                          setPersonae([...personae, rest]);
                          setPersonaSubView("list");
                        }} className="w-full text-left p-2 rounded-lg border hover:border-[#988EF2] hover:bg-purple-50 transition-all">
                          <p className="text-sm font-medium">{getPersonaLabel(p) || t("wizard.interChallenge.personaLabel")} ({p.personaType})</p>
                          <p className="text-[10px] text-muted-foreground">{t("wizard.interChallenge.from")}: {p.challengeTitle}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ===== STEP 5: Cas de simulation ===== */}
          {step === 4 && (
            <div className="space-y-3">
              {/* Liste des cas existants */}
              {editingTargetIdx === null && (
                <>
                  <p className="text-sm text-muted-foreground">
                    {t("wizard.interChallenge.targetsIntro")}
                  </p>
                  {targets.map((target, i) => (
                    <div key={i} className="p-3 rounded-lg border bg-gray-50 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold" style={{ color: "#384872" }}>{target.caseType} {!target.caseType.includes("B2C") && target.companySize ? `— ${localizeCompanySize(target.companySize, i18n.language)}` : ""}</span>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7" onClick={() => { setTargetForm({ ...target }); setEditingTargetIdx(i); }}>
                            <Pencil className="h-3 w-3 text-gray-600" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7" onClick={() => setTargets(targets.filter((_, j) => j !== i))}>
                            <Trash2 className="h-3 w-3 text-red-400" />
                          </Button>
                        </div>
                      </div>
                      {target.industry.length > 0 && <p className="text-[10px] text-muted-foreground">{t("wizard.interChallenge.industryLabel")}: {target.industry.map((ind: string) => localizeIndustry(ind, i18n.language)).join(", ")}</p>}
                      <p className="text-[10px] text-muted-foreground">{t("wizard.interChallenge.offersLabel")}: {target.offerIndexes.length > 0 ? target.offerIndexes.map(idx => offers[idx]?.name).filter(Boolean).join(", ") : t("wizard.interChallenge.allOffers")}</p>
                      <p className="text-[10px] text-muted-foreground">{t("wizard.interChallenge.personaeLabel")}: {target.personaeIndexes.length > 0 ? target.personaeIndexes.map(idx => personae[idx] ? getPersonaLabel(personae[idx]) : null).filter(Boolean).join(", ") : t("wizard.interChallenge.allPersonae")}</p>
                    </div>
                  ))}
                  <Button variant="outline" onClick={() => { setTargetForm({ caseType: "B2B", companySize: "", industry: [], offerIndexes: [], personaeIndexes: [] }); setEditingTargetIdx(-1); }}>
                    <Plus className="h-4 w-4 mr-1" />{t("wizard.interChallenge.addSimulationCase")}
                  </Button>
                </>
              )}

              {/* Formulaire création/édition d'un cas */}
              {editingTargetIdx !== null && (
                <div className="space-y-3">
                  <button onClick={() => setEditingTargetIdx(null)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                    <ChevronLeft className="h-3 w-3" />{t("wizard.interChallenge.backToList")}
                  </button>
                  <h3 className="text-sm font-semibold" style={{ color: "#384872" }}>
                    {editingTargetIdx === -1 ? t("wizard.interChallenge.newSimulationCase") : t("wizard.interChallenge.editCase", { number: editingTargetIdx + 1 })}
                  </h3>

                  {/* Type B2B/B2C */}
                  <div>
                    <Label className="text-xs">{t("wizard.interChallenge.clientTypeLabel")}</Label>
                    <div className="flex gap-2 mt-1">
                      {["B2B", "B2C"].map((tp) => (
                        <Button key={tp} size="sm" variant={targetForm.caseType === tp ? "default" : "outline"}
                          className={targetForm.caseType === tp ? "bg-[#384872] text-white" : ""}
                          onClick={() => setTargetForm({ ...targetForm, caseType: tp })}>{tp === "B2B" ? t("wizard.interChallenge.clientTypeB2B") : t("wizard.interChallenge.clientTypeB2C")}</Button>
                      ))}
                    </div>
                  </div>

                  {/* Taille entreprise (B2B) */}
                  {targetForm.caseType === "B2B" && (
                    <div>
                      <Label className="text-xs">{t("wizard.interChallenge.companySizeLabel")}</Label>
                      <Select value={targetForm.companySize} onValueChange={(v) => setTargetForm({ ...targetForm, companySize: v })}>
                        <SelectTrigger><SelectValue placeholder={t("wizard.interChallenge.selectPlaceholder")} /></SelectTrigger>
                        <SelectContent>{COMPANY_SIZE_OPTIONS.map((opt) => <SelectItem key={opt} value={opt}>{localizeCompanySize(opt, i18n.language)}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Secteur d'activité (B2B) */}
                  {targetForm.caseType === "B2B" && (
                    <div>
                      <Label className="text-xs">{t("wizard.interChallenge.industriesLabel")}</Label>
                      <div className="max-h-[120px] overflow-y-auto rounded border p-2 mt-1">
                        <div className="flex flex-wrap gap-1">
                          {INDUSTRY_OPTIONS.map((ind) => (
                            <button key={ind} onClick={() => {
                              const has = targetForm.industry.includes(ind);
                              setTargetForm({ ...targetForm, industry: has ? targetForm.industry.filter(x => x !== ind) : [...targetForm.industry, ind] });
                            }} className={`px-2 py-0.5 text-[10px] rounded border transition-all ${
                              targetForm.industry.includes(ind) ? "border-[#384872] bg-blue-50 font-semibold" : "border-gray-200 hover:border-gray-300"
                            }`}>{localizeIndustry(ind, i18n.language)}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sélection offres */}
                  <div>
                    <Label className="text-xs">{t("wizard.interChallenge.associatedOfferLabel")}</Label>
                    {offers.length === 0 ? (
                      <p className="text-[10px] text-muted-foreground mt-1">{t("wizard.interChallenge.noOffersGoBack")}</p>
                    ) : (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {offers.map((o, oi) => (
                          <button key={oi} onClick={() => {
                            const has = targetForm.offerIndexes.includes(oi);
                            setTargetForm({ ...targetForm, offerIndexes: has ? [] : [oi] });
                          }} className={`px-2 py-1 text-xs rounded-lg border-2 transition-all ${
                            targetForm.offerIndexes.includes(oi) ? "border-[#7CBF94] bg-green-50 font-semibold" : "border-gray-200 hover:border-gray-300"
                          }`}>{o.name}</button>
                        ))}
                      </div>
                    )}
                    {targetForm.offerIndexes.length === 0 && offers.length > 0 && (
                      <p className="text-[10px] text-muted-foreground italic mt-0.5">{t("wizard.interChallenge.noOfferSelectedHint")}</p>
                    )}
                  </div>

                  {/* Sélection personae */}
                  <div>
                    <Label className="text-xs">{t("wizard.interChallenge.associatedPersonaeLabel")}</Label>
                    {personae.length === 0 ? (
                      <p className="text-[10px] text-muted-foreground mt-1">{t("wizard.interChallenge.noPersonaeGoBack")}</p>
                    ) : (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {personae.map((p, pi) => (
                          <button key={pi} onClick={() => {
                            const has = targetForm.personaeIndexes.includes(pi);
                            setTargetForm({ ...targetForm, personaeIndexes: has ? targetForm.personaeIndexes.filter(x => x !== pi) : [...targetForm.personaeIndexes, pi] });
                          }} className={`px-2 py-1 text-xs rounded-lg border-2 transition-all ${
                            targetForm.personaeIndexes.includes(pi) ? "border-[#988EF2] bg-purple-50 font-semibold" : "border-gray-200 hover:border-gray-300"
                          }`}>{getPersonaLabel(p) || t("wizard.interChallenge.personaLabel")} ({p.personaType})</button>
                        ))}
                      </div>
                    )}
                    {targetForm.personaeIndexes.length === 0 && personae.length > 0 && (
                      <p className="text-[10px] text-muted-foreground italic mt-0.5">{t("wizard.interChallenge.noPersonaSelectedHint")}</p>
                    )}
                  </div>

                  {/* Bouton valider */}
                  <Button onClick={() => {
                    if (editingTargetIdx === -1) {
                      setTargets([...targets, { ...targetForm }]);
                    } else {
                      const upd = [...targets];
                      upd[editingTargetIdx] = { ...targetForm };
                      setTargets(upd);
                    }
                    setEditingTargetIdx(null);
                  }} disabled={targetForm.caseType === "B2B" && (!targetForm.companySize || targetForm.industry.length === 0)}
                    className="bg-[#384872] hover:bg-[#384872]/90 text-white">
                    <Check className="h-4 w-4 mr-1" />
                    {editingTargetIdx === -1 ? t("wizard.interChallenge.addThisCase") : t("common.save")}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ===== STEP 6: Récompense + cadre ===== */}
          {step === 5 && (
            <div className="space-y-4">
              {/* Cadre */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("wizard.interChallenge.scopeLabel")}</Label>
                <div className="flex gap-2 mt-1">
                  <Button size="sm" variant={reward.scope === "inter_public" ? "default" : "outline"}
                    className={reward.scope === "inter_public" ? "bg-[#F6C847] text-[#926B00]" : ""}
                    onClick={() => setReward({ ...reward, scope: "inter_public" })}>
                    <Globe className="h-3 w-3 mr-1" />{t("wizard.interChallenge.scopePublic")}
                  </Button>
                  <Button size="sm" variant={reward.scope === "inter_cross_clients" ? "default" : "outline"}
                    className={reward.scope === "inter_cross_clients" ? "bg-[#F6C847] text-[#926B00]" : ""}
                    onClick={() => setReward({ ...reward, scope: "inter_cross_clients" })}>
                    <Users className="h-3 w-3 mr-1" />{t("wizard.interChallenge.scopeSelectedClients")}
                  </Button>
                </div>
              </div>

              {/* Badge */}
              {badges.length > 0 && (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("wizard.interChallenge.badgeLabel")}</Label>
                  <Select value={reward.badgeId} onValueChange={(v) => setReward({ ...reward, badgeId: v === "_none" ? "" : v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder={t("wizard.interChallenge.noBadge")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">{t("wizard.interChallenge.noBadge")}</SelectItem>
                      {badges.map((b: any) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Cadeau */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("wizard.interChallenge.giftLabel")}</Label>
                <div className="grid grid-cols-1 gap-2 mt-1">
                  <Input value={reward.giftName} onChange={(e) => setReward({ ...reward, giftName: e.target.value })} placeholder={t("wizard.interChallenge.giftNamePlaceholder")} />
                  <Input value={reward.giftDescription} onChange={(e) => setReward({ ...reward, giftDescription: e.target.value })} placeholder={t("wizard.interChallenge.descriptionLabel")} />
                  <Input value={reward.giftPhotoUrl} onChange={(e) => setReward({ ...reward, giftPhotoUrl: e.target.value })} placeholder={t("wizard.interChallenge.giftPhotoUrlPlaceholder")} />
                </div>
              </div>

              {/* Récap */}
              <div className="p-3 rounded-lg border bg-gray-50 space-y-1">
                <p className="text-xs font-semibold" style={{ color: "#384872" }}>{t("wizard.interChallenge.summaryTitle")}</p>
                <p className="text-xs">📝 {form.title}</p>
                <p className="text-xs">🏢 {company.name}</p>
                <p className="text-xs">📦 {offers.length} {t("wizard.interChallenge.offersUnit")}</p>
                <p className="text-xs">👤 {personae.length} {t("wizard.interChallenge.personaeUnit")}</p>
                <p className="text-xs">🎯 {targets.length || t("wizard.interChallenge.allCombinations")} {targets.length ? t("wizard.interChallenge.simulationCases") : ""}</p>
                <p className="text-xs">📅 {form.startDate} → {form.endDate}</p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-3 mt-3">
              <AlertCircle className="h-4 w-4 shrink-0" /><p>{error}</p>
            </div>
          )}
        </div>

        {/* Footer — navigation */}
        <div className="flex items-center justify-between px-6 py-3 border-t bg-gray-50/50 shrink-0">
          <Button variant="outline" onClick={() => step > 0 ? setStep(step - 1) : onClose()} disabled={saving}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            {step > 0 ? t("wizard.interChallenge.previous") : t("common.cancel")}
          </Button>

          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canNext()}
              style={{ backgroundColor: "#F6C847", color: "#926B00" }} className="hover:opacity-90">
              {t("wizard.interChallenge.next")}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSave} disabled={saving || !canNext()}
              style={{ backgroundColor: "#7CBF94", color: "#fff" }} className="hover:opacity-90">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <Check className="h-4 w-4 mr-1" />
              {isEditing
                ? t("common.save")
                : t("wizard.interChallenge.createChallenge")}
            </Button>
          )}
        </div>

        {/* Resize */}
        <div className="absolute top-0 right-0 w-1.5 h-full cursor-ew-resize hidden sm:block" onPointerDown={onPointerDown("right")} />
        <div className="absolute bottom-0 left-0 w-full h-1.5 cursor-ns-resize hidden sm:block" onPointerDown={onPointerDown("bottom")} />
        <div className="absolute bottom-0 right-0 w-5 h-5 cursor-nwse-resize hidden sm:flex items-end justify-end p-0.5" onPointerDown={onPointerDown("corner")}>
          <svg width="10" height="10" viewBox="0 0 10 10" className="text-gray-400">
            <circle cx="8" cy="2" r="1" fill="currentColor" /><circle cx="8" cy="5.5" r="1" fill="currentColor" />
            <circle cx="8" cy="9" r="1" fill="currentColor" /><circle cx="4.5" cy="5.5" r="1" fill="currentColor" />
            <circle cx="4.5" cy="9" r="1" fill="currentColor" /><circle cx="1" cy="9" r="1" fill="currentColor" />
          </svg>
        </div>
      </div>
    </div>
  );
}
